import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, CheckCircle2, Mail } from "lucide-react";
import SEO from "../components/SEO";
import { api, formatApiError } from "../lib/api";
import { PHONE, PHONE_TEL } from "../data/content";
import { trackEvent } from "../lib/analytics";

// Google Ads conversion hooks — inactive until the owner supplies conversion IDs.
// Set REACT_APP_ADS_FORM_CONVERSION / REACT_APP_ADS_CALL_CONVERSION (format
// "AW-XXXXXXXXX/LabelXYZ") and the gtag conversion fires automatically.
const ADS_FORM_CONVERSION = process.env.REACT_APP_ADS_FORM_CONVERSION;
const ADS_CALL_CONVERSION = process.env.REACT_APP_ADS_CALL_CONVERSION;

function fireAdsConversion(sendTo) {
  if (sendTo && typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", { send_to: sendTo });
  }
}

// Fires the callback-enquiry conversion at most once per browser session,
// and only after the backend has confirmed the enquiry was saved.
function fireFormConversionOnce() {
  try {
    if (sessionStorage.getItem("te_conv_fired")) return;
    sessionStorage.setItem("te_conv_fired", "1");
  } catch {}
  fireAdsConversion(ADS_FORM_CONVERSION);
}

const ATTRIBUTION_PARAMS = ["gclid", "gbraid", "wbraid", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ATTRIBUTION_PARAMS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v;
    });
    const stored = JSON.parse(sessionStorage.getItem("te_attribution") || "{}");
    const merged = { ...stored, ...found };
    if (!merged.landing_url) merged.landing_url = window.location.href.slice(0, 500);
    if (!merged.referrer && document.referrer) merged.referrer = document.referrer.slice(0, 500);
    sessionStorage.setItem("te_attribution", JSON.stringify(merged));
    return merged;
  } catch {
    return {};
  }
}

export default function AdelaideEnquiry() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", suburb: "", message: "", preferred_contact: "phone", website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { captureAttribution(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onCallClick = () => {
    trackEvent("phone_call_click", { location: "adelaide_enquiry" });
    fireAdsConversion(ADS_CALL_CONVERSION);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const attribution = captureAttribution();
      await api.post("/enquiries/adelaide", { ...form, attribution });
      setDone(true);
      trackEvent("adelaide_enquiry_submitted", { preferred_contact: form.preferred_contact });
      fireFormConversionOnce();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please call us on 1800 836 390.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--te-bg)", color: "var(--te-text)" }}>
      <SEO
        title="Tech Emergency Adelaide — Your Local Technology Specialist"
        description="Locally owned and operated in Adelaide. Friendly, professional technology assistance with a personal approach. Request a callback or call 1800 836 390."
        path="/adelaide-enquiry"
        noIndex
      />

      {/* Minimal header — no site navigation */}
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0 pr-3">
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: "var(--te-primary)" }}>
              <span className="text-sm font-bold text-white">T</span>
            </div>
            <div>
              <div className="font-bold tracking-tight leading-none">TECH EMERGENCY</div>
              <div className="te-overline text-[10px] whitespace-nowrap">Adelaide · IT Support</div>
            </div>
          </div>
          <a
            href={PHONE_TEL}
            onClick={onCallClick}
            data-testid="ael-header-call-btn"
            className="te-btn te-btn-primary text-sm px-4 py-2.5 whitespace-nowrap"
          >
            <Phone size={14} /> <span className="hidden sm:inline">{PHONE}</span><span className="sm:hidden">Call us</span>
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pb-16">
        {done ? (
          <div className="pt-12 text-center" data-testid="ael-success-message">
            <CheckCircle2 size={48} className="mx-auto mb-5" style={{ color: "var(--te-success, #34C759)" }} />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">Thank you for contacting Tech Emergency.</h1>
            <p className="text-zinc-300 leading-relaxed max-w-md mx-auto mb-8">
              We've received your enquiry and will be in touch as soon as possible.
            </p>
            <a href={PHONE_TEL} onClick={onCallClick} data-testid="ael-success-call-btn" className="te-btn te-btn-primary px-8 py-4">
              <Phone size={16} /> Call {PHONE}
            </a>
          </div>
        ) : (
          <>
            {/* Hero */}
            <section className="pt-4 sm:pt-8 pb-3 sm:pb-5 text-center sm:text-left">
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight leading-tight mb-1" data-testid="ael-h1">
                Tech Emergency Adelaide
              </h1>
              <p className="text-base sm:text-xl font-medium" style={{ color: "var(--te-primary)" }} data-testid="ael-h2">
                Your Local Technology Specialist
              </p>
            </section>

            {/* Enquiry form */}
            <section id="enquiry-form" className="te-card p-4 sm:p-7" style={{ backgroundColor: "var(--te-paper)" }}>
              <h2 className="text-lg sm:text-2xl font-bold tracking-tight mb-1">Request a callback</h2>
              <p className="text-sm text-zinc-400 mb-4 hidden sm:block">Tell us how to reach you and we'll be in touch as soon as possible.</p>

              <form onSubmit={submit} data-testid="ael-form" className="space-y-3">
                <label className="block">
                  <div className="text-xs font-bold text-white uppercase tracking-wider mb-1.5">Full name <span style={{ color: "var(--te-primary)" }}>*</span></div>
                  <input required maxLength={120} value={form.name} onChange={set("name")} autoComplete="name"
                    className="ael-input" data-testid="ael-name-input" />
                </label>
                <label className="block">
                  <div className="text-xs font-bold text-white uppercase tracking-wider mb-1.5">Mobile or telephone number <span style={{ color: "var(--te-primary)" }}>*</span></div>
                  <input required type="tel" minLength={6} maxLength={40} value={form.phone} onChange={set("phone")} autoComplete="tel" inputMode="tel"
                    className="ael-input" data-testid="ael-phone-input" />
                </label>
                <label className="block">
                  <div className="text-xs font-bold text-white uppercase tracking-wider mb-1.5">Email address <span style={{ color: "var(--te-primary)" }}>*</span></div>
                  <input required type="email" maxLength={200} value={form.email} onChange={set("email")} autoComplete="email" inputMode="email"
                    className="ael-input" data-testid="ael-email-input" />
                </label>
                <label className="block">
                  <div className="text-xs font-bold text-white uppercase tracking-wider mb-1.5">How can we help? (optional)</div>
                  <textarea rows={2} maxLength={2000} value={form.message} onChange={set("message")}
                    className="ael-input" data-testid="ael-message-input" />
                </label>

                <div>
                  <div className="text-xs font-bold text-white uppercase tracking-wider mb-1.5">Preferred contact method</div>
                  <div className="flex gap-3">
                    {["phone", "email"].map((m) => (
                      <label key={m} className={`flex-1 flex items-center justify-center gap-2 border px-4 py-2.5 cursor-pointer text-sm font-medium focus-within:ring-2 focus-within:ring-[var(--te-primary)] ${form.preferred_contact === m ? "border-[var(--te-primary)] text-white" : "border-white/15 text-zinc-400"}`}>
                        <input
                          type="radio" name="preferred_contact" value={m}
                          checked={form.preferred_contact === m}
                          onChange={set("preferred_contact")}
                          className="sr-only"
                          data-testid={`ael-contact-${m}`}
                        />
                        {m === "phone" ? <Phone size={14} /> : <Mail size={14} />}
                        {m === "phone" ? "Phone" : "Email"}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Honeypot — hidden from humans, bots fill it */}
                <input
                  type="text" name="website" value={form.website} onChange={set("website")}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                />

                {error && <div className="text-sm" style={{ color: "var(--te-primary)" }} data-testid="ael-form-error">{error}</div>}

                <button type="submit" disabled={submitting} data-testid="ael-submit-btn"
                  className="te-btn te-btn-primary w-full justify-center text-base py-3.5 disabled:opacity-60">
                  {submitting ? "Sending…" : "Press to Submit"}
                </button>

                <p className="text-[11px] text-zinc-500 leading-snug">
                  We only use your details to respond to your enquiry. We never sell or share
                  your information. See our{" "}
                  <Link to="/privacy" target="_blank" className="underline underline-offset-2 text-zinc-400 hover:text-white" data-testid="ael-privacy-link">
                    Privacy Policy
                  </Link>.
                </p>
              </form>
            </section>
          </>
        )}
      </main>

      <footer className="border-t border-white/10 py-6">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center text-xs text-zinc-500">
          Tech Emergency · Adelaide, South Australia ·{" "}
          <a href={PHONE_TEL} onClick={onCallClick} className="text-zinc-400 hover:text-white">{PHONE}</a>{" "}
          · <Link to="/privacy" target="_blank" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>
        </div>
      </footer>

      <style>{`.ael-input { width:100%; background: var(--te-surface); border:1px solid rgba(255,255,255,0.85); color:var(--te-text); padding:0.65rem 0.8rem; font-family:'IBM Plex Sans',sans-serif; font-size:16px; outline:none; } .ael-input:focus { border-color: var(--te-primary); }`}</style>
    </div>
  );
}
